import './index.scss'
import text from './text.json'
import {PageSubTitle, PageTitle} from '../../components/core/title'
import ResponsivePageTitle from '../../components/core/title/ResponsivePageTitle'
import Footer from '../../components/layouts/footer'
import Table from '../../components/core/table'
import ContentLoader from '../../components/core/loader'
import {AutomationIcon, EditIcon, HorizontalMoreIcon, InfoIcon, PlusIcon, TrashIcon} from '../../icons'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {deleteAutomation, getAutomations, getEvents, setCheckedData} from '../../stores/automation'
import EmptyContent from '../resources/EmptyContent'
import {ImgButton} from '../../components/core/button'
import CreateAutomation from './CreateAutomation'
import UpdateAutomation from './UpdateAutomation'
import {Link} from 'react-router-dom'
import NavigationHelper from '../../utils/navigation'
import {routes} from '../../routes'
import {setConfirmIsActive} from '../../stores/alert'
import Confirm from '../../components/core/confirm'
import Tooltip from '../../components/core/tooltip'
import Modal from '../../components/core/modal'
import Dropdown from '../../components/core/dropdown'
import {MenuDivider, MenuItem} from '@szhsin/react-menu'
import {colors} from '../../utils/colors'
import classNames from 'classnames'

const Automations = () => {
    const dispatch = useDispatch()
    const data = useSelector(state => state.automation.automations)
    const events = useSelector(state => state.automation.events)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const isFetching = useSelector(state => state.automation.isFetching)
    const isDeleting = useSelector(state => state.automation.isDeleting)
    const checkedData = useSelector(state => state.automation.checkedData)
    const [createPopupIsActive, setCreatePopupIsActive] = useState(false)
    const [updatePopupIsActive, setUpdatePopupIsActive] = useState(false)
    const checkedAutomation = data?.data?.automations?.find(item => item.id === checkedData[0])
    const [clickedAutomation, setClickedAutomation] = useState(null)
    const tableHeader = [
        {
            label: 'Name',
            className: 'min-width'
        },
        {
            label: 'Description',
            className: 'min-width'
        },
        {
            label: 'Triggers',
            className: 'flex-1'
        }
    ]

    const tbodyData = []

    const handleCreateButtonOnclick = () => {
        setCreatePopupIsActive(!createPopupIsActive)
    }

    const handleEditButtonOnclick = () => {
        setClickedAutomation(null)
        setUpdatePopupIsActive(!updatePopupIsActive)
    }

    const getVisitButton = id => {
        return (
            <span className="button-field">
                <Link
                    to={NavigationHelper.getNavigationUrl(routes.automation_designer.path, ':id', id)}
                >
                    <AutomationIcon/>
                </Link>
            </span>
        )
    }

    const deleteCheckedAutomation = () => {
        const success = dispatch(deleteAutomation(clickedAutomation?.id || checkedAutomation?.id))
        success && dispatch(setConfirmIsActive(false))
        setClickedAutomation(null)
    }

    const generateTriggerNames = automation => {
        return `
            <span class="event-names">
                ${automation?.events?.map(item => `<span>- ${item?.name}</span>`).join('')}
            </span>
            `
    }

    const getActionButton = data => {
        return <Dropdown menuButton={<button className={classNames('table-action-btn', {
            dark: isDarkMode
        })}><HorizontalMoreIcon/></button>}>
            <MenuItem onClick={() => {
                setClickedAutomation(data)
                setUpdatePopupIsActive(true)
            }
            }>
                <span className="d-flex"><EditIcon/></span>
                <span>{text.edit}</span>
            </MenuItem>
            <MenuDivider/>
            <MenuItem className="danger" onClick={() => {
                setClickedAutomation(data)
                dispatch(setConfirmIsActive(true))
            }
            }>
                <span className="d-flex"><TrashIcon stroke={colors.colorRed}/></span>
                <span>{text.delete}</span>
            </MenuItem>
        </Dropdown>
    }

    const generateTriggers = automation => {
        return (
            <span className="flex-1">
                <div className="events">
                    <span>{`${automation?.events?.length} ${text.event_selected}`}</span>
                    <span>
                        <Tooltip
                            trigger="click"
                            content={<span dangerouslySetInnerHTML={{__html: generateTriggerNames(automation)}}/>}
                            interactive={true}
                            classname="max-width-unset"
                        >
                            <InfoIcon color={colors.colorMuted}/>
                        </Tooltip>
                    </span>
                </div>
                <div className="d-flex align-items-center gap-half">
                    {getVisitButton(automation?.id)}
                    {getActionButton(automation)}
                </div>
            </span>
        )
    }

    const openConfirm = () => {
        setClickedAutomation(null)
        dispatch(setConfirmIsActive(true))
    }

    const generateAutomations = () => {
        return (
            <>
                <div className="search-filter-actions">
                    <div className="selected-actions">
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}
                        onClick={handleCreateButtonOnclick}
                        >
                            <PlusIcon stroke={colors.colorMuted}/>
                            <span className="button-text">{text.new}</span>
                        </ImgButton>
                        <ImgButton className={classNames({
                            'light-bg': !isDarkMode
                        })}
                        disabled={checkedData?.length !== 1}
                        onClick={handleEditButtonOnclick}>
                            <EditIcon/>
                            <span className="button-text">{text.edit}</span>
                        </ImgButton>
                        <ImgButton className={classNames('outline-red', {
                            'light-bg': !isDarkMode
                        })}
                        disabled={checkedData?.length !== 1}
                        onClick={openConfirm}>
                            <span><TrashIcon stroke={colors.colorRed}/></span>
                            <span className="button-text">{text.delete}</span>
                        </ImgButton>
                    </div>
                </div>
                {!data?.data ?
                    <EmptyContent message={data?.message} hideButton={true}/>
                    : (
                        <div className="content-body">
                            <Table
                                tableHeader={tableHeader}
                                tbodyData={tbodyData}
                                data={data?.data?.automations}
                                hasCheckbox={true}
                                isMultiCheckable={false}
                                identificator='id'
                                inputName='automation'
                                selectedData={checkedData}
                                setSelectedData={data => dispatch(setCheckedData(data))}
                            />
                        </div>
                    )
                }
            </>
        )
    }

    useEffect(() => {
        dispatch(getAutomations())
        dispatch(getEvents())
    }, [])

    data?.data?.automations?.map(item => {
        tbodyData.push([
            <span className="min-width">{item?.name}</span>,
            <span className="min-width">{item?.description}</span>,
            generateTriggers(item)
        ])
    })

    const closePopup = () => {
        dispatch(getAutomations(false))
        handleCreateButtonOnclick()
        dispatch(setCheckedData([]))
    }

    const closeEditPopup = () => {
        dispatch(getAutomations(false))
        handleEditButtonOnclick()
    }

    return (
        <main className={classNames('main automations', {
            dark: isDarkMode
        })}>
            <Confirm
                body={text.are_you_sure}
                callback={deleteCheckedAutomation}
                showLoader={isDeleting}
            />
            <Modal title={text.add_new_automation} closeModal={handleCreateButtonOnclick}
                isOpen={createPopupIsActive}>
                <CreateAutomation closePopup={closePopup} events={events}/>
            </Modal>
            <Modal title={text.edit_automation} closeModal={handleEditButtonOnclick}
                isOpen={updatePopupIsActive}>
                <UpdateAutomation closePopup={closeEditPopup} events={events}
                    setClickedAutomation={setClickedAutomation}
                    automation={clickedAutomation || checkedAutomation}/>
            </Modal>
            <div>
                <div>
                    <ResponsivePageTitle/>
                    <div className="content-title">
                        <div className="left-side">
                            <div>
                                <PageSubTitle>
                                    <p className={classNames('section', {
                                        dark: isDarkMode
                                    })}>{text.section}</p>
                                </PageSubTitle>
                                <PageTitle>
                                    <h1  className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}>{text.title}</h1>
                                </PageTitle>
                            </div>
                        </div>
                        <ImgButton onClick={handleCreateButtonOnclick}>
                            {text.add_new_automation}
                            <span><PlusIcon/></span>
                        </ImgButton>
                    </div>
                </div>
                {
                    isFetching ? <ContentLoader/> : generateAutomations()
                }
            </div>
            <Footer/>
        </main>
    )
}

export default Automations
